import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@heroui/input";
import { Button } from "@heroui/button";
import { Sun, Moon } from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const { login, loading, error } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get the expired flag from URL if present
  const tokenExpired = searchParams.get("expired") === "true";
  // Get the 'from' param which tells us where the user was trying to go
  const from = searchParams.get("from") || "/dashboard";

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted:", { username, password });

    // Basic form validation
    const errors: Record<string, string> = {};

    if (!username.trim()) {
      errors.username = "Username is required";
    }

    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);

      return;
    }

    // Clear previous form errors
    setFormErrors({});

    // Submit login
    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      console.error("Login error:", err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 px-4">
      <div className="absolute top-4 right-4">
        <Button
          isIconOnly
          aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
          variant="light"
          onPress={toggleTheme}
        >
          {theme === "light" ? (
            <Moon className="w-6 h-6" />
          ) : (
            <Sun className="w-6 h-6" />
          )}
        </Button>
      </div>

      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-8">
          <div className="text-center mb-8">
            <img
              alt="Enzo's Restaurant"
              className="h-24 mx-auto rounded-full border-2 border-red-600"
              src="/logo.svg"
            />
            <h2 className="text-2xl font-bold mt-4 text-gray-900 dark:text-white">
              Welcome back
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              Login to your account
            </p>
          </div>

          {/* Token expired message */}
          {tokenExpired && (
            <div className="bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 p-4 rounded-lg mb-6">
              Your session has expired. Please login again.
            </div>
          )}

          {/* Error message from auth context */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <Input
                errorMessage={formErrors.username}
                isInvalid={!!formErrors.username}
                label="Username"
                placeholder="Enter your username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            <div>
              <Input
                errorMessage={formErrors.password}
                isInvalid={!!formErrors.password}
                label="Password"
                placeholder="Enter your password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {/* This can be replaced with Switch component from HeroUI */}
                <input
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                />
                <label
                  className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                  htmlFor="remember-me"
                >
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link
                  className="text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
                  to="/forgot-password"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>

            <Button
              fullWidth
              className="py-2"
              color="primary"
              isLoading={loading}
              type="submit"
            >
              Log in
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
